/**
 * Two defects of copy assembled from pieces, both shipped by ITC Tax run 5.
 *
 *   1. A description that says the intro again. Ad unit 6e478896: the intro
 *      opened "ITC Tax reports $5M in tax savings for a Texas data center
 *      client." and the description was "ITC Tax reports a Texas data center
 *      client saved $5M in tax savings." A description is the third line under
 *      the headline; repeating the intro spends it on nothing.
 *   2. A source fragment lifted whole whose grammar belonged to the sentence it
 *      came from. The same unit's intro: "We pursue valuations with how your
 *      buildings, infrastructure, and equipment actually operate." The page
 *      writes "...by pursuing valuations with how your buildings...", and cut
 *      out of that sentence the clause no longer parses.
 *
 * Both are deterministic. The lift check fires only on a narrow construction
 * AND a long verbatim run from the evidence, so a sentence the writer composed
 * ("Start with how your assets actually operate") is never blocked. Pure. The
 * caller decides what a finding does (the claims gate blocks).
 */

import type { AdCopyClaimsField, AdCopyClaimsFinding } from './ad-copy-claims';

const FUNCTION_WORDS = new Set(
  'a an the of and or to in on for with by at from that this these those is are was were be been it its as their your our we you they them than but not no who which what when where how new also into over only more most such each any all can may will would could should has have had do does did'.split(
    ' ',
  ),
);

function stem(word: string): string {
  return word.length > 3 && /[^s]s$/.test(word) ? word.slice(0, -1) : word;
}

function contentWords(text: string, excluded: ReadonlySet<string>): Set<string> {
  const words = text.toLowerCase().match(/[\p{L}\p{N}$%]+/gu) ?? [];
  return new Set(
    words
      .map(stem)
      .filter((word) => word.length >= 3 && !FUNCTION_WORDS.has(word) && !excluded.has(word)),
  );
}

/** Share of the description's content words already in the intro that makes it a repeat. */
const REPEAT_SHARE = 0.6;
/** A description with fewer content words than this has too little to be a repeat of anything. */
const REPEAT_MIN_WORDS = 3;

export function findDescriptionRepeatingIntro(
  fields: readonly AdCopyClaimsField[],
  brandName: string | null,
  platform: 'linkedin' | 'meta' | 'google_rsa',
): AdCopyClaimsFinding[] {
  if (platform !== 'linkedin') return [];
  const intro = fields.find((field) => field.field === 'commentary')?.text ?? '';
  const description = fields.find((field) => field.field === 'description')?.text ?? '';
  if (!intro.trim() || !description.trim()) return [];
  const excluded = new Set((brandName?.toLowerCase().match(/[\p{L}\p{N}]+/gu) ?? []).map(stem));
  const descriptionWords = contentWords(description, excluded);
  if (descriptionWords.size < REPEAT_MIN_WORDS) return [];
  const introWords = contentWords(intro, excluded);
  const shared = [...descriptionWords].filter((word) => introWords.has(word));
  if (shared.length / descriptionWords.size < REPEAT_SHARE) return [];
  return [
    {
      field: 'description',
      detail: `The description repeats the intro text: ${shared.length} of its ${descriptionWords.size} content words are already in the intro ("${description.trim()}").`,
      fix: 'Write the description as one new line under the headline that the intro does not already say: the offer, the deliverable or one different proof point from the evidence. Leave it out if the evidence offers nothing new.',
    },
  ];
}

/**
 * `with how <clause>` after a noun: "valuations with how your buildings
 * operate". After these verbs it is ordinary English ("start with how your
 * assets operate"), so they are left alone.
 */
const WITH_HOW_CLAUSE =
  /\b([\p{L}-]+)\s+with\s+how\s+(?:your|our|their|its|the|you|we|they|each|every)\b/iu;
const VERBS_THAT_TAKE_WITH_HOW = new Set(
  'start starts started starting begin begins began beginning work works worked working deal deals dealing align aligns aligned aligning contrast contrasts compare compares compared comparing reckon reckons come comes coming square squares agree agrees agreed concerned happy familiar'.split(
    ' ',
  ),
);

/** Words in a row the sentence must share with one evidence line before it counts as a lift. */
const LIFT_MIN_WORDS = 6;

function words(text: string): string[] {
  return text.toLowerCase().match(/[\p{L}\p{N}$%]+/gu) ?? [];
}

function sharesLongRun(sentence: string, sources: readonly string[]): boolean {
  const sentenceWords = words(sentence);
  for (const source of sources) {
    const sourceWords = words(source);
    for (let start = 0; start + LIFT_MIN_WORDS <= sentenceWords.length; start += 1) {
      const run = sentenceWords.slice(start, start + LIFT_MIN_WORDS).join(' ');
      for (let from = 0; from + LIFT_MIN_WORDS <= sourceWords.length; from += 1) {
        if (sourceWords.slice(from, from + LIFT_MIN_WORDS).join(' ') === run) return true;
      }
    }
  }
  return false;
}

function sentencesOf(text: string): string[] {
  return text
    .split(/\n+/)
    .flatMap((paragraph) => paragraph.split(/(?<=[.!?])\s+(?=[A-Z0-9"'“(])/))
    .map((sentence) => sentence.trim())
    .filter(Boolean);
}

export function findUngrammaticalSourceLifts(
  fields: readonly AdCopyClaimsField[],
  sources: readonly string[],
): AdCopyClaimsFinding[] {
  const findings: AdCopyClaimsFinding[] = [];
  for (const { field, text } of fields) {
    if (!text) continue;
    for (const sentence of sentencesOf(text)) {
      const match = WITH_HOW_CLAUSE.exec(sentence);
      if (!match || VERBS_THAT_TAKE_WITH_HOW.has(match[1]!.toLowerCase())) continue;
      if (!sharesLongRun(sentence, sources)) continue;
      findings.push({
        field,
        detail: `${field} lifts a source fragment whose grammar belongs to the sentence it was cut from ("${sentence}"): "${match[0].trim()}" does not parse on its own.`,
        fix: `Say the fact in a sentence of your own: keep what the source states (what is valued, and that the valuation follows how the equipment is actually used) and drop the borrowed "with how" clause.`,
        sentence,
      });
    }
  }
  return findings;
}
